import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const socket = io(process.env.REACT_APP_SOCKET_URL, {
  transports: ['websocket'],
  withCredentials: true,
  path: '/socket.io',
  autoConnect: true,
  timeout: 10000,
});

const HRPay = () => {
  const { logout } = useContext(AuthContext) || {};
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const chatBoxRef = useRef(null);

  // Quick replies
  const quickReplies = [
    "How do I check my pay stub?",
    "What's the vacation policy?",
    "How do I update my personal information?",
    "When is the next payday?"
  ];

  // Determine mobile
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Mount animations
  useEffect(() => {
    const t = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Socket listeners
  useEffect(() => {
    const onChatResponse = (response) => {
      const formatted = formatAIResponse(response);
      setMessages((prev) => [
        ...prev,
        {
          text: formatted.text,
          sender: "assistant",
          feedback: null,
          ticketLink: null,
          responseType: formatted.type,
          citations: formatted.citations || [],
          links: formatted.links || [],
        },
      ]);
      setIsTyping(false);
    };

    const onTyping = (typing) => setIsTyping(typing);

    socket.on("chat_response", onChatResponse);
    socket.on("typing_indicator", onTyping);

    return () => {
      socket.off("chat_response", onChatResponse);
      socket.off("typing_indicator", onTyping);
    };
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Custom styles (scrollbar + responsive + animations)
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      #chat-messages-container::-webkit-scrollbar { width: 8px; height: 8px; }
      #chat-messages-container::-webkit-scrollbar-track { background: rgba(255,255,255,.3); border-radius: 10px; }
      .dark #chat-messages-container::-webkit-scrollbar-track { background: rgba(30,30,30,.5); border-radius: 10px; }
      #chat-messages-container::-webkit-scrollbar-thumb { background: #FFF000; border-radius: 10px; border: 2px solid transparent; background-clip: padding-box; }
      #chat-messages-container::-webkit-scrollbar-thumb:hover { background: #FFFF33; }
      #chat-messages-container { scrollbar-width: thin; scrollbar-color: #FFF000 rgba(255,255,255,.3); }
      .dark #chat-messages-container { scrollbar-width: thin; scrollbar-color: #FFF000 rgba(30,30,30,.5); }

      @media (max-width: 768px) {
        .chat-header-buttons { flex-direction: column; gap: 8px; align-items: flex-end; }
        .quick-replies-grid { grid-template-columns: 1fr !important; }
        .message-text { font-size: .9rem !important; }
        .logo-image { height: 45px !important; }
        .welcome-image { height: 120px !important; }
      }
      @media (max-width: 480px) {
        .chat-container { margin: 0 !important; border-radius: 0 !important; height: 100% !important; max-height: 100% !important; }
        .main-wrapper { padding: 0 !important; }
        .header-wrapper { padding: 8px !important; }
        .input-wrapper { padding: 8px !important; }
        .chat-messages-wrapper { padding: 12px !important; }
        .message-bubble { max-width: 85% !important; padding: 8px !important; }
      }

      @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes slideIn { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes scaleIn { from { transform: scale(.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      @keyframes fadeInLogo { 0% { opacity: 0; transform: translateY(-10px); } 100% { opacity: 1; transform: translateY(0); } }

      .message-enter { animation: fadeIn .5s ease-out forwards; }
      .quick-reply-enter { animation: scaleIn .4s ease-out forwards; }
      .delayed-animation-1 { opacity: 0; animation: fadeIn .5s ease-out forwards; animation-delay: .1s; }
      .delayed-animation-3 { opacity: 0; animation: fadeIn .5s ease-out forwards; animation-delay: .3s; }
      .delayed-animation-4 { opacity: 0; animation: fadeIn .5s ease-out forwards; animation-delay: .4s; }
      .chat-container-enter { animation: scaleIn .7s cubic-bezier(.23,1,.32,1) forwards; }
      .header-enter { animation: slideIn .6s cubic-bezier(.23,1,.32,1) forwards; }
    `;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  // --- Actions ---
  const sendMessage = () => {
    const text = query.trim();
    if (!text) return;
    setMessages((m) => [...m, { text, sender: "user" }]);
    socket.emit("query", text);
    setQuery("");
    setIsTyping(true);
  };

  const sendQuickReply = (text) => {
    setMessages((m) => [...m, { text, sender: "user" }]);
    socket.emit("query", text);
    setIsTyping(true);
  };

  const handleEnter = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleLogout = async () => {
    try {
      await logout?.();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const sendFeedback = async (feedbackType, question, answer, index) => {
    try {
      const res = await axios.post("https://apihrpay.aiunvrshub.com/api/hrpay/feedback", {
        feedback: feedbackType,
        question,
        answer,
      });
      setMessages((prev) =>
        prev.map((msg, i) =>
          i === index
            ? { ...msg, feedback: feedbackType, ticketLink: res.data.ticketLink || null }
            : msg
        )
      );
    } catch (err) {
      console.error("Error submitting feedback:", err);
    }
  };

  // --- Formatting ---
  const formatAIResponse = (raw) => {
    const out = { text: raw, type: "text", citations: [], links: [] };
    const citationRegex = /<cite:(\d+)>(.*?)<\/cite>/g;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let formatted = raw;

    // citations
    formatted = formatted.replace(citationRegex, (_, id, txt) => {
      out.citations.push({ id, text: txt });
      return `<span class="citation-text">${txt}<sup class="citation-number">[${id}]</sup></span>`;
    });

    // links
    formatted = formatted.replace(linkRegex, (_, text, url) => {
      out.links.push({ text, url });
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-yellow-500 underline hover:text-yellow-700">${text}</a>`;
    });

    // markdown-lite
    formatted = formatted
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/`(.*?)`/g, '<code class="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-sm">$1</code>')
      .replace(/^# (.*?)$/gm, '<h3 class="text-xl font-bold mb-2">$1</h3>')
      .replace(/^## (.*?)$/gm, '<h4 class="text-lg font-bold mb-2">$1</h4>')
      .replace(/^- (.*?)$/gm, '<li class="ml-4">• $1</li>')
      .replace(/\n\s*\n/g, "</p><p class='mb-3'>")
      .replace(/\n/g, "<br>");

    if (!formatted.startsWith("<h") && !formatted.startsWith("<p")) {
      formatted = `<p class='mb-3'>${formatted}</p>`;
    }

    out.text = formatted;
    return out;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`flex justify-between items-center p-2 sm:p-3 header-wrapper ${showContent ? "header-enter" : "opacity-0"}`}>
        <div className="flex items-center gap-2" />
        <div className="flex gap-2 chat-header-buttons">
          <button
            className="bg-laryzeDarkGray text-uasYellow dark:bg-uasYellow dark:text-laryzeDarkGray hover:bg-uasGray hover:text-white px-2 sm:px-3 py-1 rounded-md text-sm sm:text-base font-normal transition-all duration-300"
            onClick={() => {
              setMessages([]);
              setQuery("");
            }}
          >
            New chat
          </button>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-2 sm:px-3 py-1 rounded-md text-sm sm:text-base font-normal hover:bg-red-700 transition-all duration-300"
            aria-label="Logout"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main chat */}
      <div className={`flex-1 flex justify-center items-center p-3 ${showContent ? "" : "opacity-0"} h-full flex-col`}>
        <div
          id="chatbox"
          className={`w-full max-w-4xl flex flex-grow flex-col rounded-lg overflow-hidden h-full bg-white/70 dark:bg-laryzeDarkGray/80 backdrop-blur-sm shadow-lg ${
            showContent ? "chat-container-enter" : ""
          }`}
        >
          {/* Messages */}
          <div id="chat-messages-container" ref={chatBoxRef} className="flex-1 p-6 pt-6 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col justify-center items-center text-center">
                <img
                  className={`h-40 w-auto mb-4 mt-4 ${showContent ? "delayed-animation-1" : "opacity-0"}`}
                  alt="uas-text-logo"
                  src="/HR-Pay-AI-assist-animated-logoa.gif"
                />
                <h3 className={`mb-8 text-m font-regular sm:text-lg text-laryzeDarkGray dark:text-white ${showContent ? "delayed-animation-3" : "opacity-0"}`}>
                  Hello! How can I assist you today?
                </h3>

                <div
                  className={`grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 w-full max-w-lg sm:max-w-3xl quick-replies-grid ${
                    showContent ? "delayed-animation-4" : "opacity-0"
                  }`}
                >
                  {quickReplies.map((text, i) => (
                    <button
                      key={i}
                      onClick={() => sendQuickReply(text)}
                      className="p-2 sm:p-3 text-sm rounded-lg border text-center font-medium
                                 bg-white text-laryzeDarkGray dark:bg-laryzeDarkGray dark:text-white
                                 border-uasYellow hover:border-uasYellow hover:ring-2 sm:hover:ring-4 hover:ring-uasYellow hover:ring-opacity-40
                                 transition-all duration-200 outline-none quick-reply-enter"
                      style={{ animationDelay: `${0.5 + i * 0.1}s` }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`mb-3 sm:mb-4 ${msg.sender === "user" ? "flex justify-end" : "flex justify-start"} message-enter`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div
                    className={`${msg.sender === "user" ? "max-w-[100%]" : "max-w-[100%]"} flex-grow rounded-lg p-2 sm:p-3 message-bubble ${
                      msg.sender === "user"
                        ? "bg-white text-laryzeDarkGray shadow-sm dark:bg-uasGray dark:text-white"
                        : "bg-transparent border border-gray-200 text-laryzeDarkGray dark:border-gray-700 dark:text-white"
                    }`}
                  >
                    {msg.sender === "assistant" && (
                      <div className="flex items-center mb-2 pb-1 border-b border-gray-200 dark:border-gray-700">
                        <div className="w-6 h-6 rounded-full mr-2 flex-shrink-0 overflow-hidden">
                          <img src="/HR-Pay-AI-assist-animated-logoa.gif" alt="AI Assistant" className="w-full h-full object-cover" />
                        </div>
                        <div className="text-xs opacity-80 font-medium">HRPAY</div>
                      </div>
                    )}

                    <div
                      className={`message-content text-sm sm:text-md ${msg.sender === "assistant" ? "leading-relaxed" : "font-semibold"}`}
                      dangerouslySetInnerHTML={{
                        __html: msg.text.replace(/\n\s*\n/g, "<br><br>").replace(/\n/g, "<br>"),
                      }}
                    />

                    {msg.sender === "assistant" && msg.citations?.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-medium mb-1 opacity-75">Sources:</div>
                        <div className="text-xs space-y-1">
                          {msg.citations.map((c, i) => (
                            <div key={i} className="flex">
                              <span className="mr-1">[{c.id}]</span>
                              <span className="opacity-75">{c.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {msg.sender === "assistant" && msg.feedback === null && index !== 0 && (
                      <div className="flex mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 justify-end space-x-3">
                        <button
                          onClick={() => sendFeedback("thumbs_up", messages[index - 1]?.text, msg.text, index)}
                          className="text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200 flex items-center text-xs"
                          aria-label="Thumbs up"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                          Helpful
                        </button>
                        <button
                          onClick={() => sendFeedback("thumbs_down", messages[index - 1]?.text, msg.text, index)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200 flex items-center text-xs"
                          aria-label="Thumbs down"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                          </svg>
                          Not helpful
                        </button>
                      </div>
                    )}

                    {msg.ticketLink && (
                      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs mb-2 text-gray-600 dark:text-gray-300">Need further assistance with this issue?</p>
                        <a
                          href={msg.ticketLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-uasYellow text-laryzeDarkGray hover:bg-yellow-400 transition-colors duration-200"
                        >
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                          </svg>
                          Create Support Ticket
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {isTyping && (
              <div className="flex mb-4 message-enter">
                <div className="rounded-lg p-2 sm:p-3 bg-gray-100 dark:bg-laryzeDarkGray">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className={`p-2 sm:p-4 border-t border-gray-200 dark:border-gray-700 input-wrapper ${showContent ? "delayed-animation-3" : "opacity-0"}`}>
            <div className="relative flex">
              <input
                type="text"
                className="flex-1 py-2 sm:py-3 px-3 sm:px-4 pr-10 rounded-lg 
                           bg-white text-gray-800 border-gray-200 dark:bg-laryzeDarkGray dark:text-white dark:border-gray-600
                           border focus:ring-2 focus:border-yellow-400 dark:focus:border-yellow-400 
                           focus:ring-uasYellow focus:ring-opacity-40 transition-all duration-200 outline-none text-sm sm:text-base"
                placeholder="Ask a question"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleEnter}
              />
              <button
                onClick={sendMessage}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 sm:p-2 
                           bg-laryzeDarkGray text-uasYellow dark:bg-uasYellow dark:text-laryzeDarkGray 
                           hover:text-white hover:bg-uasGray transition-all duration-200 rounded-lg"
                aria-label="Send"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRPay;
